<template>
  <base-modal-ex :active.sync="active"
    :animation-in="animationIn"
    :animation-out="animationOut"
    :animation-duration="animationDuration"
    :can-cancel="false"
    :min-width="80"
    has-modal-card>
    <header class="modal-card-head">
      <p class="modal-card-title">Contacts</p>
    </header>
    <section class="modal-card-body">
      <div class="columns">
        <div class="column is-two-thirds">
          <div class="field">
            <label class="label">Title</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="contact.contact.title">
                  <option value="null"
                    disabled>Select</option>
                  <option v-for="(name, code) in salutes"
                    :key="code"
                    :value="name">
                    {{ name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">First Name</label>
            <div class="control">
              <input class="input"
                v-model="contact.contact.firstName"
                type="text"
                :class="{ 'is-danger' : $v.contact.contact.firstName.$error }"
                @input="$v.contact.contact.firstName.$touch()">
              <span class="help is-danger"
                v-if="! $v.contact.contact.firstName.required">First Name is required</span>
            </div>
          </div>
          <div class="field">
            <label class="label">Last Name</label>
            <div class="control">
              <input class="input"
                v-model="contact.contact.lastName"
                type="text"
                :class="{ 'is-danger' : $v.contact.contact.lastName.$error }"
                @input="$v.contact.contact.lastName.$touch()">
              <span class="help is-danger"
                v-if="! $v.contact.contact.lastName.required">Last Name is required</span>
            </div>
          </div>
          <div class="field">
            <user-asset-addresses ref="addresses"
              :addresses="activeAddresses"
              :country-code="sortedAddresses?.countryCode??null"
              :required="true"
              :asset-type-name="'addresses'"
              @removeAddress="removeAddress"
              @setDefault="setDefault"
              @addTmpAddress="addTmpAddress"
              @validationChanged="onRefsValidationChanged" />
          </div>
          <div class="field">
            <user-asset-phones ref="phones"
              :phones="activePhones"
              :required="true"
              :asset-type-name="'phones'"
              @removePhone="removePhone"
              @setDefault="setDefault"
              @addTmpPhone="addTmpPhone"
              @validationChanged="onRefsValidationChanged" />
          </div>
          <div class="field">
            <user-asset-emails ref="emails"
              :emails="activeEmails"
              :required="true"
              :asset-type-name="'emails'"
              @removeEmail="removeEmail"
              @setDefault="setDefault"
              @addTmpEmail="addTmpEmail"
              @validationChanged="onRefsValidationChanged" />
          </div>
        </div>
        <div class="column">
          <div class="field">
            <label class="label">Contents</label>
            <p class="control">
              <textarea class="textarea"
                v-model="contact.contact.remarks.remarks"
                @change="contact.contact.isDirty = true" />
            </p>
          </div>

          <div class="field">
            <p class="control">
              <a class="button is-primary"
                @click="showRecurringRemark">
                <span class="icon is-small">
                  <i class="mdi mdi-repeat" />
                </span>
                <span> Recurring Remarks</span>
              </a>
            </p>
          </div>

          <div class="field">
            <label class="label">Alert?</label>
            <div class="control">
              <input id="alert"
                name="alert"
                class="switch is-rounded"
                type="checkbox"
                v-model="contact.contact.remarks.isAlert">
              <label for="alert" />
            </div>
          </div>
        </div>
      </div>
    </section>
    <footer class="modal-card-foot is-flex is-justify-content-flex-end">
      <div class="buttons">
        <button class="button is-primary tooltip"
          data-tooltip="Save"
          :disabled="validationErrors"
          @click="save">
          <span class="icon">
            <span class="mso mso-20px mso-fill">
              save
            </span>
          </span>
          <span>Save</span>
        </button>
        <button class="button tooltip"
          @click="close"
          data-tooltip="Cancel">
          <span class="icon">
            <span class="mso mso-20px">
              close
            </span>
          </span>
          <span>Cancel</span>
        </button>
      </div>
    </footer>
  </base-modal-ex>
</template>

<script>
import { BaseModalEx } from '@/components/BulmaModal'
import { Salutation } from '../../../enums'
import UserAssetAddresses from './UserAssetAddresses.vue'
import { AddressModel, EmailModel, PhoneModel } from '../../../classes/viewmodels'
import UserAssetPhones from './UserAssetPhones.vue'
import UserAssetEmails from './UserAssetEmails.vue'
import Vue from 'vue'
import RecurringRemarkModal from '@/views/recurringremarks/RecurringRemarkModal'
import { required } from 'vuelidate/lib/validators'

const RecurringRemarkComponent = Vue.extend(RecurringRemarkModal)

export default {
  name: 'ContactModal',
  components: {
    BaseModalEx,
    UserAssetAddresses,
    UserAssetPhones,
    UserAssetEmails
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    contact: {
      type: Object,
      default: () => ({})
    },
    animationIn: {
      type: String,
      default: 'zoomIn'
    },
    animationOut: {
      type: String,
      default: 'zoomOut'
    },
    animationDuration: {
      type: [String, Number],
      default: 0.3
    }
  },
  data() {
    return {
      cardModal: null,
      refsHasError: false
    }
  },
  computed: {
    salutes() {
      return Salutation
    },
    sortedAddresses() {
      if (this.contact && this.contact.contact.addresses) {
        return [...this.contact.contact.addresses].sort((a, b) => b.isDefault - a.isDefault)
      }
    },
    validationErrors() {
      return (
        this.$v.contact.contact.$error ||
        (this.$refs.addresses && this.$refs.addresses.$v && this.$refs.addresses.$v.$error) ||
        (this.$refs.phones && this.$refs.phones.$v && this.$refs.phones.$v.$error) ||
        (this.$refs.emails && this.$refs.emails.$v && this.$refs.emails.$v.$error) ||
        this.refsHasError
      )
    },
    activeAddresses() {
      if (this.contact && this.contact.contact.addresses) {
        return [...this.contact.contact.addresses].filter((a) => !a.isDeleted)
      }
    },
    activePhones() {
      if (this.contact && this.contact.contact.phones) {
        return [...this.contact.contact.phones].filter((a) => !a.isDeleted)
      }
    },
    activeEmails() {
      if (this.contact && this.contact.contact.emails) {
        return [...this.contact.contact.emails].filter((a) => !a.isDeleted)
      }
    }
  },
  mounted() {
    this.$v.$touch()
  },
  methods: {
    close() {
      this.$emit('close')
    },
    save() {
      this.$emit('save', this.contact)
    },
    setDefault(asset, assetType) {
      this.contact.contact[assetType].forEach((a) => (a.isDefault = a == asset))
    },
    addTmpAddress() {
      let newAddress = new AddressModel(this.contact.companyId)
      if (this.contact.contact.addresses.length == 0) {
        newAddress.isDefault = true
      }
      this.contact.contact.addresses.push(newAddress)
    },
    addTmpPhone() {
      let newPhone = new PhoneModel()
      if (this.contact.contact.phones.length == 0) {
        newPhone.isDefault = true
      }
      this.contact.contact.phones.push(newPhone)
    },
    addTmpEmail() {
      let newEmail = new EmailModel()
      if (this.contact.contact.emails.length == 0) {
        newEmail.isDefault = true
      }
      this.contact.contact.emails.push(newEmail)
    },
    removeAddress(address) {
      // this.contact.contact.addresses = this.contact.contact.addresses.filter((a) => a !== address)
      const idx = this.contact.contact.addresses.findIndex((a) => a === address)
      if (idx !== -1) {
        if (!address.isNew && address.id) {
          this.contact.contact.addresses[idx].isDeleted = true
        } else {
          this.contact.contact.addresses.splice(idx, 1)
        }
      }
    },
    removePhone(phone) {
      //this.contact.contact.phones = this.contact.contact.phones.filter((p) => p !== phone)
      const idx = this.contact.contact.phones.findIndex((a) => a === phone)
      if (idx !== -1) {
        if (!phone.isNew && phone.id) {
          this.contact.contact.phones[idx].isDeleted = true
        } else {
          this.contact.contact.phones.splice(idx, 1)
        }
      }
    },
    removeEmail(email) {
      // this.contact.contact.emails = this.contact.contact.emails.filter((e) => e !== email)
      const idx = this.contact.contact.emails.findIndex((a) => a === email)
      if (idx !== -1) {
        if (!email.isNew && email.id) {
          this.contact.contact.emails[idx].isDeleted = true
        } else {
          this.contact.contact.emails.splice(idx, 1)
        }
      }
    },
    showRecurringRemark() {
      this.cardModal = new RecurringRemarkComponent({
        el: document.createElement('div'),
        propsData: {
          visible: false
        }
      }).$on('ok', (res) => {
        this.contact.contact.remarks.remarks = res + '\n' + this.contact.contact.remarks.remarks
        this.contact.contact.remarks.isDirty = true
      })

      this.cardModal.visible = true
    },
    onRefsValidationChanged(hasError) {
      this.refsHasError = hasError
    }
  },
  validations() {
    return {
      contact: {
        contact: {
          firstName: { required },
          lastName: { required }
        }
      }
    }
  }
}
</script>
